import { useMemo } from 'react'
import { useHydrated } from '@/hooks/use-hydrated'
import { useTranslation } from 'react-i18next'
import { LanguageCode, Translatable, Bundle, Tariff } from '@/types'

type LocalizedBundle = {
  [K in keyof Bundle]: Bundle[K] extends Translatable<infer T>
    ? T
    : Bundle[K] extends Array<Tariff>
      ? Array<LocalizedTariff>
      : Bundle[K]
}

type LocalizedTariff = {
  [K in keyof Tariff]: Tariff[K] extends Translatable<infer T> ? T : Tariff[K]
} & { name: string }

function localizeField<T>(
  field: Translatable<T> | T,
  language: LanguageCode,
  fallbackLanguage: LanguageCode
): T | undefined {
  if (typeof field === 'object' && field !== null && 'en' in field) {
    return (field[language] ?? field[fallbackLanguage]) as T
  }
  return field as T
}

export const useLocalizedBundle = (bundle: Bundle): LocalizedBundle => {
  const { i18n } = useTranslation()

  const localizedBundle = useMemo(() => {
    const language = i18n.language as LanguageCode
    const fallbackLanguage: LanguageCode = 'en'

    return Object.entries(bundle).reduce((acc, [key, value]) => {
      if (
        (key === 'tariffs' || key === 'regions' || key === 'attributes') &&
        Array.isArray(value)
      ) {
        // @ts-ignore
        acc[key] = value.map((tariff) =>
          Object.entries(tariff).reduce((tariffAcc, [tariffKey, tariffValue]) => {
            ;(tariffAcc as any)[tariffKey] = localizeField(tariffValue, language, fallbackLanguage)
            return tariffAcc
          }, {} as LocalizedTariff)
        )
      } else {
        acc[key] = localizeField(value, language, fallbackLanguage)
      }
      return acc
    }, {} as LocalizedBundle)
  }, [bundle, i18n.language])

  return localizedBundle
}

/**
 * A translated value in the viewer's language.
 *
 * The language is only known in the browser, so the server and the first pass
 * over its HTML use English: that is what a search engine reads, and the two
 * agree. After that it is read during render, not in an effect, so a component
 * mounted later has its text on its first frame -- anything that measures or
 * animates it sees the finished layout, not an empty one.
 */
function useLocalizedValue<T>(translations: Translatable<T> | null | undefined): T | null {
  const { i18n } = useTranslation()
  const hydrated = useHydrated()

  if (!translations) {
    return null
  }

  if (!hydrated) {
    return translations['en'] ?? null
  }

  return translations[i18n.language as LanguageCode] ?? translations['en'] ?? null
}

export default useLocalizedValue
